import moment from 'moment';

const key = (name) => (name || '').trim().replace(/\s+/g, ' ').toLowerCase();

/** Keeps the later of `day` and what `map` holds under `value`. */
const keep = (map, value, day) => {
	if(day && (!map.has(value) || day > map.get(value))) {
		map.set(value, day);
	}
};

/** A map of 'Y-m-d's by the normalized names of `byName`'s keys, the latest where two meet. */
const byKey = (byName) => {
	const found = new Map();
	Object.keys(byName || {}).forEach((name) => keep(found, key(name), byName[name]));
	return found;
};

/**
 * The last day each of an idea's ingredients was bought, by ingredient id, from the food store's
 * last-bought maps, which the server keeps over every bought list the budget has, however old, and
 * from the bought lists the store holds, which carry a tick the maps have not heard of yet.
 *
 * The same thing is the same grocery item when the ingredient says which, as the item editor counts
 * it, along with rows of the same name that say no item; and the same name otherwise, ignoring case
 * and spacing, so an ingredient shares its history with the same thing typed by hand. An ingredient
 * left as a generic with kinds, any cereal, was bought whenever one of its kinds was, as the item
 * editor counts it too; its kinds are the catalog's (`groceryItems`), and the ones the idea itself
 * names.
 *
 * - `lastBoughtByItem`: grocery item id → 'Y-m-d', over bought rows that say which item they are
 * - `lastBoughtByUnlinkedName`: lowercased name → 'Y-m-d', over bought rows that do not
 * - `lastBoughtByName`: the same, and also the active list's names over every bought row, item or
 *   not. Only for an ingredient that names no item, since another item can share a name.
 * - `groceryItems`: the catalog by id, for an item's kinds and for an ingredient that names no item,
 *   whose name may be an item's
 * - `groceryLists`, `groceryRows`: the lists and rows held, whose bought ones count as well
 *
 * @param {Array<{id: number, name: string, grocery_item_id?: number, generic?: ?{id: number}, kinds?: Array<{id: number}>}>} ingredients
 * @param {Object|null|undefined} entities  the food store's
 * @returns {Map<number, moment>}  only ingredients that have been bought
 */
export default function ingredientsLastBought(ingredients, entities) {
	const found = new Map();
	if(!entities) {
		return found;
	}
	const byItem = new Map(Object.keys(entities.lastBoughtByItem || {}).map((id) => [Number(id), entities.lastBoughtByItem[id]]));
	const byUnlinkedName = byKey(entities.lastBoughtByUnlinkedName);
	const byName = byKey(entities.lastBoughtByName);
	byUnlinkedName.forEach((day, name) => keep(byName, name, day));
	const lists = entities.groceryLists || {};
	Object.values(entities.groceryRows || {}).forEach((row) => {
		if(typeof row.list_key !== 'string' || !row.list_key.startsWith('bought:')) {
			return;
		}
		const day = (lists[row.list_key] && lists[row.list_key].purchased_on) || row.list_key.slice('bought:'.length);
		const name = key(row.name);
		keep(byName, name, day);
		if(row.grocery_item_id) {
			keep(byItem, Number(row.grocery_item_id), day);
		} else {
			keep(byUnlinkedName, name, day);
		}
	});
	const catalog = Object.values(entities.groceryItems || {});
	const kindsOf = (itemId) => catalog.filter((item) => Number(item.kind_of_id) === itemId).map((item) => Number(item.id));
	const itemsNamed = (name) => catalog.filter((item) => key(item.name) === name).map((item) => Number(item.id));

	for(const ingredient of ingredients) {
		const name = key(ingredient.name);
		let days;
		if(ingredient.grocery_item_id) {
			const itemId = Number(ingredient.grocery_item_id);
			const named = !!ingredient.generic && Number(ingredient.generic.id) === itemId ?
				(ingredient.kinds || []).map((kind) => Number(kind.id)) : [];
			days = [itemId, ...kindsOf(itemId), ...named].map((id) => byItem.get(id)).concat(byUnlinkedName.get(name));
		} else {
			days = itemsNamed(name).map((id) => byItem.get(id)).concat(byName.get(name));
		}
		days = days.filter(Boolean);
		if(days.length) {
			found.set(ingredient.id, moment(days.sort()[days.length - 1], 'YYYY-MM-DD'));
		}
	}
	return found;
}
